import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';

import { AuthLayout } from '@cold-ambulance/layout';
import { Button, CardContent, Label } from '@cold-ambulance/shadcn';
import {
  EmailInput,
  FieldMessage,
  FormMessage,
  PasswordInput,
  errorToFormIssue,
  signInEmail,
} from '@cold-ambulance/ui';

import { auth } from '../firebase.tsx';

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'البريد الإلكتروني مطلوب' })
    .email({ message: 'البريد الإلكتروني غير صحيح' }),
  password: z.string().min(1, { message: 'كلمة المرور مطلوبة' }),
});

export default function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      email: import.meta.env.DEV ? 'vendor@ca.com' : '',
      password: import.meta.env.DEV ? 'vendor@ca.com' : '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        try {
          await signInEmail(auth, props.value.email, props.value.password);
          await navigate('/');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      stepIndex={2}
      className="bg-[#F8F8F8]"
      title={t('signin.title')}
      subtitle={
        <div className="flex gap-x-1">
          <p>{t('signin.noAccount')}</p>
          <Link to={'/signup'} className="text-primary">
            {t('signin.registerWithUs')}
          </Link>
        </div>
      }
    >
      <CardContent className="px-0">
        <form
          className="mt-6 mb-4 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="email"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email">
                  {t('common.email')}
                </Label>
                <EmailInput
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email-password">
                  {t('common.password')}
                </Label>
                <PasswordInput
                  id="email-password"
                  placeholder="・・・・・・"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <Link
                  to="/forgot-password"
                  className="text-primary text-sm font-medium"
                >
                  {t('signin.forgotPassword')}
                </Link>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit}
                className="mt-8 w-full cursor-pointer"
              >
                {isSubmitting ? t('signin.signingIn') : t('common.signIn')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
